import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SurveyTemplate from "../SurveyTemplate";
import "../../styles/components/MultiPageSurvey.css";

const EnergyQuizPage3 = ({
  quizData,
  updateQuizData,
  onComplete,
  onNavigationBack,
  currentSection,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [localData, setLocalData] = useState({
    fermentedFoods: quizData.fermentedFoods || "",
    hydrationElectrolytes: quizData.hydrationElectrolytes || "",
    supplementStack: quizData.supplementStack || [],
    biologicalSex: quizData.biologicalSex || "",
    age: quizData.age || "",
    email: quizData.email || "",
  });
  const [ageError, setAgeError] = useState("");

  const supplementOptions = [
    "Magnesium L-Threonate", "Magnesium Bisglycinate", "Apigenin", "Creatine", 
    "Rhodiola Rosea", "Ashwagandha", "Omega-3 EPA", "Vitamin D3 + K2", 
    "AG1/Greens", "LMNT/Electrolytes", "None"
  ];

  const questions = [
    {
      id: "fermentedFoods",
      question: "How often do you eat fermented foods?",
      intro: "Here's what the Stanford microbiome study found — fermented foods > fiber for gut diversity.",
      options: [
        { value: "daily", label: "🥬 Daily (kimchi, kefir, sauerkraut)" },
        { value: "few_times", label: "🥪 A few times per week" },
        { value: "occasionally", label: "🤔 Occasionally" },
        { value: "rarely", label: "❌ Rarely or never" },
      ],
      info: "Huberman cites Stanford research: Fermented foods increase microbiome diversity more effectively than fiber supplements. Aim for 6 servings/day for maximum benefit.",
    },
    {
      id: "hydrationElectrolytes",
      question: "How's your hydration and electrolyte game?",
      intro: "Both Huberman and Sims emphasize this — LMNT-style optimization.",
      options: [
        { value: "optimal", label: "💧 Optimized with electrolytes" },
        { value: "adequate", label: "💦 Adequate water, some electrolytes" },
        { value: "water_only", label: "🚰 Just water, no added electrolytes" },
        { value: "poor", label: "😅 Probably dehydrated" },
      ],
      info: "Proper hydration requires sodium, potassium, magnesium. Both experts recommend early morning salt water and electrolyte replenishment for optimal cellular function.",
    },
    {
      id: "supplementStack",
      question: "What's in your current supplement stack?",
      intro: "Let's see what research-backed tools you're already using.",
      type: "multiSelect",
      info: "These supplements have strong research backing from Huberman Lab and performance literature. We'll recommend gaps based on your specific needs.",
    },
    {
      id: "biologicalSex",
      question: "What's your biological sex?",
      intro: "This helps us tailor recommendations — women are not small men.",
      options: [
        { value: "female", label: "♀️ Female" },
        { value: "male", label: "♂️ Male" },
        { value: "other", label: "⚧️ Prefer not to say" },
      ],
      info: "Dr. Stacy Sims' research shows sex-specific differences in metabolism, training response, and nutritional needs that require personalized approaches.",
    },
    {
      id: "age",
      question: "What's your age?",
      intro: "Age affects everything from sleep architecture to nutrient absorption.",
      type: "number",
      placeholder: "Enter your age",
      info: "Age influences cortisol patterns, growth hormone production, muscle protein synthesis rates, and optimal training/recovery protocols.",
    },
    {
      id: "email",
      question: "Where should we send your personalized protocol?",
      intro: "Almost done! We'll email your research-backed optimization plan.",
      type: "email",
      placeholder: "your@email.com",
      info: "You'll get specific protocols, supplement recommendations with research citations, and actionable steps. No spam, ever.",
    },
  ];

  const handleSupplementToggle = (supplement) => {
    setLocalData((prev) => {
      let updated;
      if (supplement === "None") {
        updated = prev.supplementStack.includes("None") ? [] : ["None"];
      } else {
        const without = prev.supplementStack.filter((s) => s !== "None");
        updated = without.includes(supplement)
          ? without.filter((s) => s !== supplement)
          : [...without, supplement];
      }
      return { ...prev, supplementStack: updated };
    });
  };

  const validateAge = (val) => {
    const age = parseInt(val, 10);
    if (val === "") { setAgeError(""); return true; }
    if (isNaN(age) || age < 1 || age > 120) {
      setAgeError("Please enter a valid age (1–120)");
      return false;
    }
    setAgeError("");
    return true;
  };

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    if (currentQ.type === "multiSelect") return; // handled separately
    setLocalData((prev) => ({ ...prev, [currentQ.id]: value }));
    setTimeout(() => handleNext(value), 300);
  };

  const handleInputChange = (e) => {
    const currentQ = questions[currentQuestion];
    const value = e.target.value;
    setLocalData((prev) => ({ ...prev, [currentQ.id]: value }));
    if (currentQ.id === "age") validateAge(value);
  };

  const handleNext = (overrideValue) => {
    const currentQ = questions[currentQuestion];
    if (currentQ.id === "age" && !validateAge(localData.age)) return;

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Save all and complete
      Object.keys(localData).forEach((key) => {
        const val = overrideValue !== undefined && key === questions[currentQuestion].id ? overrideValue : localData[key];
        updateQuizData(key, key === "age" && val !== "" ? parseInt(val, 10) : val);
      });
      onComplete();
    }
  };

  const handleNavigationBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (onNavigationBack) {
      onNavigationBack();
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered = currentQ.type === "multiSelect"
    ? localData.supplementStack.length > 0
    : currentQ.type === "number"
      ? localData[currentQ.id] !== "" && !ageError
      : currentQ.type === "email"
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localData[currentQ.id])
        : localData[currentQ.id] !== "";
  const isLastQuestion = currentQuestion === questions.length - 1;
  const totalQuestions = 18;
  const globalIndex = 12 + currentQuestion;

  return (
    <div className="survey-page-1">
      <SurveyTemplate
        introText={currentQ.intro}
        questionText={currentQ.question}
        options={currentQ.type === "number" || currentQ.type === "email" || currentQ.type === "multiSelect" ? [] : currentQ.options || []}
        infoSections={[{ title: "Why this matters", content: currentQ.info }]}
        onOptionSelect={handleOptionSelect}
        selectedOption={currentQ.type === "number" || currentQ.type === "email" || currentQ.type === "multiSelect" ? "" : localData[currentQ.id]}
        onBack={() => onNavigationBack && onNavigationBack()}
        customInput={
          currentQ.type === "multiSelect" ? (
            <div className="custom-input-wrapper" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {supplementOptions.map((supplement) => (
                <button
                  key={supplement}
                  onClick={() => handleSupplementToggle(supplement)}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: localData.supplementStack.includes(supplement)
                      ? "2px solid #3d7a3d"
                      : "2px solid #e0e0e0",
                    background: localData.supplementStack.includes(supplement)
                      ? "rgba(61,122,61,0.08)"
                      : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "15px",
                    fontWeight: localData.supplementStack.includes(supplement) ? 600 : 400,
                    color: "#2a2d28",
                    transition: "all 0.15s ease",
                  }}
                >
                  {localData.supplementStack.includes(supplement) ? "✓ " : ""}{supplement}
                </button>
              ))}
            </div>
          ) : currentQ.type === "number" ? (
            <div className="custom-input-wrapper">
              <input
                type="number"
                value={localData[currentQ.id]}
                onChange={handleInputChange}
                placeholder={currentQ.placeholder}
                className={`option-input ${ageError ? "error" : ""}`}
                min="1"
                max="120"
              />
              {ageError && <div className="error-message">{ageError}</div>}
            </div>
          ) : currentQ.type === "email" ? (
            <div className="custom-input-wrapper">
              <input
                type="email"
                value={localData[currentQ.id]}
                onChange={handleInputChange}
                placeholder={currentQ.placeholder}
                className="option-input"
                style={{ fontSize: "16px" }}
              />
            </div>
          ) : null
        }
        progressContainer={
          <div className="progress-container">
            <div className="question-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((globalIndex + 1) / totalQuestions) * 100}%` }} />
              </div>
              <span className="progress-text">{globalIndex + 1} of {totalQuestions}</span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button className="nav-button back-button" onClick={handleNavigationBack}>
              {t("common.back", { defaultValue: "Back" })}
            </button>
            <button className="nav-button next-button" onClick={() => handleNext()} disabled={!isAnswered}>
              {isLastQuestion ? "Get My Results ✨" : t("common.next", { defaultValue: "Next" })}
            </button>
          </div>
        }
      />
    </div>
  );
};

export default EnergyQuizPage3;
